/* eslint-disable @typescript-eslint/no-explicit-any */
import { LOD, Object3D } from 'three';
import { GLTFLoaderPlugin, GLTFParser } from 'three/examples/jsm/loaders/GLTFLoader.js';

const EXTENSION_NAME = 'MSFT_lod';
const SCREENCOVERAGE_NAME = 'MSFT_screencoverage';

const LOADING_MODES = {
  All: 'all', // Default
  Any: 'any',
  Progressive: 'progressive',
};

const _map = new Map<Object3D, boolean>();

const sortChildrenOrder = (lod: LOD) => {
  for (const level of lod.levels) {
    _map.set(level.object, true);
  }
  let writeIndex = 0;
  for (let readIndex = 0; readIndex < lod.children.length; readIndex++) {
    if (!_map.has(lod.children[readIndex])) {
      lod.children[writeIndex++] = lod.children[readIndex];
    }
  }
  _map.clear();
  lod.children.length = writeIndex;
  for (const level of lod.levels) {
    lod.children.push(level.object);
  }
};

const loadScreenCoverages = (def: any) => {
  const extras = def.extras;
  const extensionsDef = def.extensions;

  if (!extras || !extras[SCREENCOVERAGE_NAME] || !extensionsDef || !extensionsDef[EXTENSION_NAME]) {
    return [];
  }

  const screenCoverages = extras[SCREENCOVERAGE_NAME];
  const levelsLength = extensionsDef[EXTENSION_NAME].ids.length + 1;

  if (
    !screenCoverages ||
    !Array.isArray(screenCoverages) ||
    screenCoverages.length !== levelsLength
  ) {
    return [];
  }

  return screenCoverages;
};

const calculateDistance = (level: number, lowestLevel: number, def: any, options: any) => {
  const coverages = loadScreenCoverages(def);
  if (options.calculateDistance) {
    return options.calculateDistance(level, lowestLevel, coverages);
  }

  if (level === 0) return 0;

  const levelsLength = def.extensions[EXTENSION_NAME].ids.length + 1;
  const c = levelsLength === coverages.length ? coverages[level - 1] : 1.0 / Math.pow(2, level * 2);

  return 1.0 / c;
};

const getLODNodeDependency = (level: number, nodeIndices: number[], parser: any) => {
  const nodeIndex = nodeIndices[level];

  if (level > 0) {
    return parser.getDependency('node', nodeIndex);
  }

  const extensions = Object.values(parser.plugins) as GLTFLoaderPlugin[];
  extensions.push(parser);
  for (const ext of extensions) {
    if (ext instanceof MSFTLod) {
      continue;
    }
    const result = ext.loadNode && ext.loadNode(nodeIndex);
    if (result) {
      return result;
    }
  }
  throw new Error('Unreachable');
};

export class MSFTLod implements GLTFLoaderPlugin {
  name: string;
  parser: GLTFParser;
  options: any;

  constructor(parser: GLTFParser, options: any = {}) {
    this.name = EXTENSION_NAME;
    this.parser = parser;
    this.options = options;
  }

  loadNode(nodeIndex: number): Promise<Object3D> | null {
    const parser = this.parser;
    const json = parser.json;

    const nodeDef = json.nodes[nodeIndex];

    if (!nodeDef.extensions || !nodeDef.extensions[this.name]) {
      return null;
    }

    const extensionDef = nodeDef.extensions[this.name];
    const nodeIndices = extensionDef.ids.slice();
    nodeIndices.unshift(nodeIndex);
    const lowestLevel = nodeIndices.length - 1;

    const lod = new LOD();
    const pending: Promise<void>[] = [];

    for (let level = 0; level < nodeIndices.length; level++) {
      pending.push(
        getLODNodeDependency(level, nodeIndices, parser).then((node: any) => {
          if (this.options.onLoadNode) {
            node = this.options.onLoadNode(lod, node, level, lowestLevel);
          }

          lod.addLevel(node, calculateDistance(level, lowestLevel, nodeDef, this.options));
          sortChildrenOrder(lod);
          if (this.options.onUpdate) {
            this.options.onUpdate(lod, node, level, lowestLevel);
          }
        }),
      );
    }

    return (
      this.options.loadingMode === LOADING_MODES.Any ? Promise.any(pending) : Promise.all(pending)
    ).then(() => lod);
  }
}

export default MSFTLod;
